/**
 * Scheduled Tasks API client.
 *
 * M-X7 (2026-05-21): UI surface for chaos.schedule_self MCP tool and
 * the /api/cards/:cardId/scheduled-tasks backend endpoints.
 *
 * IMPORTANT: apiJson already prefixes VITE_API_BASE_URL, so paths here
 * must be relative (no leading /api — that comes from the base URL).
 */
import { apiJson } from "./client";

export interface ScheduledTask {
  id: string;
  cardId: string;
  sessionId: string | null;
  createdBy: string | null;
  createdByKind: "user" | "agent" | "system";
  createdAt: string;
  scheduledAt: string;
  recurrence: string | null;
  timezone: string;
  prompt: string;
  model: string | null;
  effort: string | null;
  permissionMode: string | null;
  label: string | null;
  /**
   * Mirrors chaos.scheduled_tasks.status (scheduled_tasks_status_check).
   *
   * 'queued' was added to the DB CHECK by migration 0144 and is written by the
   * worker the moment a firing is ENQUEUED on the task queue — the task is no
   * longer 'pending' but has not started either; it advances to done/failed only
   * when its queue row reaches a real terminal state. This union had omitted it,
   * so the type claimed a value the API demonstrably returns cannot occur. It
   * did not crash (StatusBadge fell through to the default variant), which is
   * exactly why it went unnoticed.
   */
  status: "pending" | "queued" | "running" | "done" | "failed" | "cancelled";
  nextExecutionAt: string | null;
  lastExecutionAt: string | null;
  lastExecutionRunId: string | null;
  executionCount: number;
  lastError: string | null;
  notes: string | null;
}

export interface ScheduledTasksListResponse {
  items: ScheduledTask[];
}

export interface CreateScheduledTaskBody {
  scheduledAt?: string;
  delaySeconds?: number;
  recurrence?: string;
  timezone?: string;
  prompt: string;
  model?: string;
  effort?: string;
  permissionMode?: string;
  label?: string;
  notes?: string;
}

export const scheduledTasksApi = {
  list: (cardId: string) =>
    apiJson<ScheduledTasksListResponse>(`/cards/${cardId}/scheduled-tasks`),

  create: (cardId: string, body: CreateScheduledTaskBody) =>
    apiJson<{ task: ScheduledTask }>(`/cards/${cardId}/scheduled-tasks`, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { "Content-Type": "application/json" },
    }),

  cancel: (id: string) =>
    apiJson<{ task: ScheduledTask }>(`/scheduled-tasks/${id}`, {
      method: "DELETE",
    }),

  runNow: (id: string) =>
    apiJson<{ task: ScheduledTask }>(`/scheduled-tasks/${id}/run-now`, {
      method: "POST",
    }),
};

export const scheduledTasksKeys = {
  all: ["scheduled-tasks"] as const,
  list: (cardId: string) => ["scheduled-tasks", cardId] as const,
};
